import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { skipToken, useQueries, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { fetchCustomFeed } from '@/services/feedsService'
import { fetchListFeed } from '@/services/listsService'
import {
    promoteCommitted,
    sampleMixedPostsWithStats,
    selectCommitted,
    type CommittedRecord,
    type MixedFeedSnapshot,
} from '@/lib/mixedFeedSampling'
import {
    MIX_FEEDS_PER_ROUND,
    MIX_ROUND_WAIT_MS,
    MIX_TOPUP_LIMIT,
    appendSupplyPage,
    isSupplyStale,
    markSupplyFailed,
    planMixRound,
    type MixedFeedSupply,
} from '@/lib/mixedFeedSupply'
import type { Post } from '@/types/posts'
import type { ResolvedFeed } from '@/types/feeds'

export interface UseMixedTimelineOptions {
    /** Posts from the Following timeline */
    followingPosts: Post[]
    /** Pinned custom feeds to inject posts from */
    pinnedCustomFeeds: ResolvedFeed[]
    /** Whether to fetch and mix custom feed posts */
    enabled: boolean
    /** Whether the Following timeline has more pages beyond what's loaded (or is still
     * loading its first page) — see sampleMixedPosts for why this matters. */
    followingHasMore: boolean
    /** Number of Following pages loaded — each further page runs one supply round. */
    followingPageCount: number
    /** Following's `dataUpdatedAt` — a change after a Following fetch (a refresh)
     * drops stale mixed supply and runs one round. */
    followingDataUpdatedAt: number
    /** Whether Following is fetching — distinguishes a refresh from an optimistic
     * engagement write to the timeline cache (both move `dataUpdatedAt`). */
    followingIsFetching: boolean
    /** How long a round waits for its fetches before moving on (tests only). */
    roundWaitMs?: number
}

export function mixedFeedQueryKey(did: string | null, feedUri: string) {
    return ['customFeed', 'mixed', did, feedUri] as const
}

function supplyId(did: string | null, feedUri: string): string {
    return `${did}|${feedUri}`
}

/** What a round's fetches report: `visible` → `isFetching`, `more` → `isFetchingMore`. */
interface RoundKind {
    visible: boolean
    more: boolean
}
const ACTIVATION_ROUND: RoundKind = { visible: true, more: false }
const PAGE_ROUND: RoundKind = { visible: false, more: false }
const LOAD_MORE_ROUND: RoundKind = { visible: false, more: true }

/**
 * Fetches posts from the selected custom feeds a few at a time and samples
 * them into fixed slot positions within the Following timeline (see
 * @/lib/mixedFeedSampling for the sampler contract and @/lib/mixedFeedSupply
 * for the round contract). Custom feed posts are tagged with `fromFeedName`
 * for attribution display.
 *
 * Each feed's supply lives in its own React Query entry, but nothing fetches
 * declaratively: the observers below use `skipToken`, so staleTime and focus
 * refetches never fan out across every selected feed. All fetching happens in
 * serialized rounds, triggered by:
 *   T1 — activation, or a change to the selected feeds
 *   T2 — Following loading a further page
 *   T3 — Following refreshing (stale supply is reset first)
 *   T4 — `loadMore()`, called by the list once Following has no next page
 * Mirrors mobile/react-native/lib/hooks/useMixedFeedInjection.ts.
 */
export function useMixedTimeline({
    followingPosts,
    pinnedCustomFeeds,
    enabled,
    followingHasMore,
    followingPageCount,
    followingDataUpdatedAt,
    followingIsFetching,
    roundWaitMs = MIX_ROUND_WAIT_MS,
}: UseMixedTimelineOptions) {
    const { session } = useAuth()
    const queryClient = useQueryClient()
    const did = session?.did ?? null

    const active = enabled && pinnedCustomFeeds.length > 0 && !!session
    const feeds = useMemo(() => (active ? pinnedCustomFeeds : []), [active, pinnedCustomFeeds])

    const queries = useQueries({
        queries: feeds.map(feed => ({
            queryKey: mixedFeedQueryKey(did, feed.uri),
            queryFn: skipToken,
        })),
    })

    // Snapshot query data only when timestamps change — `queries` is a new array every render.
    const dataUpdatedKey = queries.map(q => q.dataUpdatedAt).join(',')

    /**
     * Identity of the arrangement: which account, over which set of feeds.
     * SORTED, so it is a membership key — reordering pinned feeds is not a
     * different selection, and must neither drop the sticky prefix nor run an
     * activation round. T1's signature is derived from this same value below
     * so the two can never disagree.
     */
    const membershipKey = useMemo(() => [...feeds.map(f => f.uri)].sort().join(','), [feeds])
    const committedKey = active ? `${did}|${membershipKey}` : null

    // Bumped by a refresh (T3). Part of the committed record's identity and of
    // the memo deps, so a refresh both invalidates the old prefix AND forces
    // the render that recomputes without it — clearing a ref alone would not,
    // because the T3 effect can return without any state change.
    const [epoch, setEpoch] = useState(0)
    // The arrangement already on screen. Written ONLY from the commit-phase
    // effect below, never from render: a render React discards (concurrent
    // abort, superseded transition, Suspense retry) must not be able to freeze
    // posts the user never saw into the sticky prefix.
    const committedRef = useRef<CommittedRecord | null>(null)

    const { posts, hasMore, remaining, injected } = useMemo(() => {
        if (!active || feeds.length === 0) {
            return {
                posts: followingPosts,
                hasMore: false,
                remaining: new Map<string, number>(),
                injected: [] as boolean[],
            }
        }
        // Keyed reads (not positional off `queries`) so a selection reorder can never
        // attribute one feed's posts to another.
        const supplies = feeds.map(feed =>
            queryClient.getQueryData<MixedFeedSupply>(mixedFeedQueryKey(did, feed.uri))
        )
        const snapshots: MixedFeedSnapshot[] = feeds.map((feed, i) => ({
            feed,
            posts: supplies[i]?.posts ?? [],
        }))
        const result = sampleMixedPostsWithStats(followingPosts, snapshots, {
            followingHasMore,
            // Reading the record during render is deliberate and safe-by-design:
            // the read is pure (selectCommitted never mutates) and gated on the
            // key + epoch, and the record is only ever WRITTEN from the
            // commit-phase effect below. A render React goes on to discard
            // therefore reads without promoting, which is precisely what stops
            // posts the user never saw from being frozen into the prefix.
            // eslint-disable-next-line react-hooks/refs
            committed: selectCommitted(committedRef.current, committedKey, epoch),
        })
        return {
            posts: result.posts,
            hasMore: supplies.some(s => !s || s.hasMore !== false),
            remaining: new Map(feeds.map((feed, i) => [feed.uri, result.remaining[i] ?? 0])),
            injected: result.injected,
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        active,
        feeds,
        did,
        followingPosts,
        dataUpdatedKey,
        queryClient,
        followingHasMore,
        committedKey,
        epoch,
    ])

    // Everything the imperative round runner needs, read at run time rather than
    // captured — a round can outlive the render that started it. Synced in effects
    // declared before the trigger effects, so triggers always see this render's values.
    const latestRef = useRef({ active, session, did, feeds, roundWaitMs })
    // Each feed's eligible posts still queued after the latest sampling pass — the
    // "ready" count the round planner uses to skip feeds that don't need a top-up.
    const remainingRef = useRef<Map<string, number>>(new Map())
    useEffect(() => {
        latestRef.current = { active, session, did, feeds, roundWaitMs }
        remainingRef.current = remaining
    })

    const feedCursorRef = useRef(0)
    // In-flight requests and reset generations, keyed by supplyId (DID + feed URI) so a
    // request still outstanding for one account never blocks or leaks into another's.
    const inFlightRef = useRef<Map<string, number>>(new Map())
    const generationRef = useRef<Map<string, number>>(new Map())
    const runningRef = useRef(false)
    const pendingRoundRef = useRef(false)
    // Which kinds of round are queued. Only activation and refresh rounds (T1/T3) count
    // toward `isFetching`, so the refresh indicator doesn't spin on every page load;
    // loadMore rounds (T4) count toward `isFetchingMore`, the list's paging state.
    const pendingVisibleRef = useRef(false)
    const pendingMoreRef = useRef(false)
    const [visibleInFlight, setVisibleInFlight] = useState(0)
    const [moreInFlight, setMoreInFlight] = useState(0)

    // Commit-phase promotion of what this render actually put on screen —
    // declared ahead of the T1/T2/T3 trigger effects so the record is already
    // current when they run, and never written from render, so a discarded
    // render cannot freeze posts the user never saw. React flushes pending
    // passive effects before processing the next update, so no render can
    // start between a commit and its promotion.
    useEffect(() => {
        committedRef.current = promoteCommitted(
            committedRef.current,
            {
                active,
                key: committedKey,
                epoch,
                followingPageCount,
                feedCount: feeds.length,
                // Only a render that actually injected something is worth
                // making sticky, and only once no activation/refresh round is
                // still establishing the arrangement. Gating on "a supply
                // cache entry exists" instead would freeze an all-Following
                // render whenever the first page failed, aged out, or deduped
                // away entirely — and the mix could then only append below the
                // whole loaded Following list, never reaching slot 16 again
                // for the session. See supplySettled.
                supplySettled: visibleInFlight === 0 && injected.includes(true),
            },
            { posts, injected }
        )
    })

    // Returns the fetch's promise plus a once-only `release` for its visible/more
    // in-flight flags, so a round that times out (see executeRound) can release the
    // flags for a fetch that is still pending instead of waiting on `finally`.
    const fetchOne = useCallback(
        (feed: ResolvedFeed, kind: RoundKind): { promise: Promise<void>; release: () => void } => {
            const { session: s, did: d } = latestRef.current
            if (!s) return { promise: Promise.resolve(), release: () => {} }
            const key = mixedFeedQueryKey(d, feed.uri)
            const id = supplyId(d, feed.uri)
            const generation = generationRef.current.get(id) ?? 0
            // A reset (T3) bumps the generation: a page requested before it is dropped
            // rather than becoming the reset feed's "fresh" first page.
            const isCurrent = () => (generationRef.current.get(id) ?? 0) === generation
            const cursor = queryClient.getQueryData<MixedFeedSupply>(key)?.cursor
            inFlightRef.current.set(id, generation)
            if (kind.visible) setVisibleInFlight(n => n + 1)
            if (kind.more) setMoreInFlight(n => n + 1)
            let released = false
            const release = () => {
                if (released) return
                released = true
                if (kind.visible) setVisibleInFlight(n => n - 1)
                if (kind.more) setMoreInFlight(n => n - 1)
            }
            const promise = (async () => {
                try {
                    const page =
                        feed.type === 'list'
                            ? await fetchListFeed(s, feed.uri, { limit: MIX_TOPUP_LIMIT, cursor })
                            : await fetchCustomFeed(s, feed.uri, { limit: MIX_TOPUP_LIMIT, cursor })
                    if (isCurrent()) {
                        queryClient.setQueryData<MixedFeedSupply>(key, prev =>
                            appendSupplyPage(prev, page, Date.now())
                        )
                    }
                } catch (err) {
                    if (isCurrent()) {
                        queryClient.setQueryData<MixedFeedSupply>(key, prev =>
                            markSupplyFailed(prev, Date.now())
                        )
                        // A failing feed silently drops out of the mix, so this is the only
                        // signal. It is marked exhausted, so each failure is logged once.
                        console.warn('[useMixedTimeline] mixed feed fetch failed', feed.uri, err)
                    }
                } finally {
                    if (inFlightRef.current.get(id) === generation) inFlightRef.current.delete(id)
                    release()
                }
            })()
            return { promise, release }
        },
        [queryClient]
    )

    const executeRound = useCallback(
        async (kind: RoundKind) => {
            const {
                active: isActive,
                did: d,
                feeds: selected,
                roundWaitMs: waitMs,
            } = latestRef.current
            if (!isActive || selected.length === 0) return
            const uris = selected.map(f => f.uri)
            const hasMoreByUri = new Map<string, boolean>()
            for (const uri of uris) {
                const supply = queryClient.getQueryData<MixedFeedSupply>(mixedFeedQueryKey(d, uri))
                if (supply) hasMoreByUri.set(uri, supply.hasMore !== false)
            }
            const inFlight = new Set(uris.filter(uri => inFlightRef.current.has(supplyId(d, uri))))
            // Walk windows until one has something to fetch (at most one full rotation), so a
            // round whose window is entirely stocked or exhausted doesn't come back empty.
            let toFetch: string[] = []
            const windows = Math.ceil(uris.length / MIX_FEEDS_PER_ROUND)
            for (let w = 0; w < windows && toFetch.length === 0; w++) {
                const plan = planMixRound(uris, feedCursorRef.current, {
                    readyByUri: remainingRef.current,
                    hasMoreByUri,
                    inFlight,
                })
                feedCursorRef.current = plan.nextFeedCursor
                toFetch = plan.toFetch
            }
            if (toFetch.length === 0) return

            const fetches = toFetch.map(uri => fetchOne(selected.find(f => f.uri === uri)!, kind))
            // Like Bluesky's REQUEST_WAIT_MS race: a slow feed doesn't hold the round up, and
            // its request isn't cancelled — a late page still lands in its cache entry.
            let timer: ReturnType<typeof setTimeout> | undefined
            await Promise.race([
                Promise.all(fetches.map(f => f.promise)),
                new Promise<void>(resolve => {
                    timer = setTimeout(resolve, waitMs)
                }),
            ])
            clearTimeout(timer)
            // A fetch that settled already released itself in `finally`; this only
            // takes effect for one still pending when the race above timed out, so
            // a slow feed doesn't hold up isFetching/isFetchingMore either.
            for (const f of fetches) f.release()
        },
        [fetchOne, queryClient]
    )

    // Rounds are serialized: a trigger arriving mid-round queues exactly one more.
    const runRound = useCallback(
        (kind: RoundKind) => {
            if (kind.visible) pendingVisibleRef.current = true
            if (kind.more) pendingMoreRef.current = true
            if (runningRef.current) {
                pendingRoundRef.current = true
                return
            }
            runningRef.current = true
            void (async () => {
                try {
                    do {
                        pendingRoundRef.current = false
                        const roundKind = {
                            visible: pendingVisibleRef.current,
                            more: pendingMoreRef.current,
                        }
                        pendingVisibleRef.current = false
                        pendingMoreRef.current = false
                        await executeRound(roundKind)
                    } while (pendingRoundRef.current)
                } finally {
                    runningRef.current = false
                }
            })()
        },
        [executeRound]
    )

    // T1 — activation or a selection change. Keyed by a signature in a ref so
    // StrictMode's double effect doesn't run two rounds. The signature is the
    // sorted membership key (see above), so reordering pinned feeds without
    // changing the set runs no round at all — it used to, which contradicted
    // the sticky prefix being kept across a reorder.
    const selectionSignature = committedKey
    const selectionSignatureRef = useRef<string | null>(null)
    useEffect(() => {
        if (selectionSignature === null) {
            selectionSignatureRef.current = null
            return
        }
        if (selectionSignatureRef.current === selectionSignature) return
        selectionSignatureRef.current = selectionSignature
        runRound(ACTIVATION_ROUND)
    }, [selectionSignature, runRound])

    // T2 / T3 — Following page events.
    const followingSignalRef = useRef<{
        pageCount: number
        dataUpdatedAt: number
        isFetching: boolean
        seenFirstPage: boolean
    }>({ pageCount: 0, dataUpdatedAt: 0, isFetching: false, seenFirstPage: false })
    useEffect(() => {
        const prev = followingSignalRef.current
        followingSignalRef.current = {
            pageCount: followingPageCount,
            dataUpdatedAt: followingDataUpdatedAt,
            isFetching: followingIsFetching,
            seenFirstPage: prev.seenFirstPage,
        }
        if (!active) {
            followingSignalRef.current.seenFirstPage = false
            return
        }
        if (followingPageCount === 0) return
        // Following's first page is covered by T1's round.
        if (!prev.seenFirstPage) {
            followingSignalRef.current.seenFirstPage = true
            return
        }
        if (prev.pageCount > 0 && followingPageCount > prev.pageCount) {
            runRound(PAGE_ROUND)
            return
        }
        const refreshed =
            followingDataUpdatedAt !== prev.dataUpdatedAt &&
            (prev.isFetching || prev.pageCount === 0)
        if (!refreshed) return
        // A refresh is meant to re-arrange: bump the generation BEFORE the
        // `resetAny` early-return below, so a refresh whose supplies are all
        // still fresh (and which therefore resets nothing) also drops the
        // sticky prefix instead of leaving the old arrangement in force.
        setEpoch(e => e + 1)
        const now = Date.now()
        const remainingAfterReset = new Map(remainingRef.current)
        let resetAny = false
        for (const feed of feeds) {
            const key = mixedFeedQueryKey(did, feed.uri)
            const supply = queryClient.getQueryData<MixedFeedSupply>(key)
            if (supply && isSupplyStale(supply, now)) {
                // Reset rather than remove: the mounted observer stays attached to the
                // same query, so the refetched page still reaches this hook.
                void queryClient.resetQueries({ queryKey: key, exact: true })
                // Drop any request still outstanding from before the reset (see fetchOne),
                // so this refresh round fetches the feed from its newest posts.
                const id = supplyId(did, feed.uri)
                generationRef.current.set(id, (generationRef.current.get(id) ?? 0) + 1)
                inFlightRef.current.delete(id)
                remainingAfterReset.delete(feed.uri)
                resetAny = true
            }
        }
        if (!resetAny) return
        remainingRef.current = remainingAfterReset
        runRound(ACTIVATION_ROUND)
    }, [
        active,
        did,
        feeds,
        followingPageCount,
        followingDataUpdatedAt,
        followingIsFetching,
        queryClient,
        runRound,
    ])

    // T4 — the list reached its end with Following exhausted.
    const loadMore = useCallback(() => {
        if (latestRef.current.active) runRound(LOAD_MORE_ROUND)
    }, [runRound])

    return {
        posts,
        isFetching: visibleInFlight > 0,
        isFetchingMore: moreInFlight > 0,
        hasMore,
        loadMore,
    }
}
